import React, { useMemo, useState } from "react";

import { useI18n } from "../i18n/I18nContext";
import {
  PLIS_TO_MM_LABEL,
  PLIS_TO_NOMINAL_MM,
  PLIS_RECONNUS,
  plisPopulationBucket,
  plisPopulationLabel,
} from "../utils/empilementPopulations";

const N_MAX = 150;
const DEFAULT_SHOW = 15;
const SHOW_OPTIONS = [10, 15, 20, 30, "tous"];
const COL_HAUT = "#1D9E75";
const COL_BAS = "#378ADD";
const PLI_TO_MM = PLIS_TO_MM_LABEL;
const PLI_TO_NOMINAL = PLIS_TO_NOMINAL_MM;

const DEFAUTS = [
  ["Delamination", "DelaminationComment", "délamination"],
  ["DefautSurface", "DefautSurfaceComment", "surface"],
  ["Planeite", "PlaneiteComment", "planéité"],
  ["AutresDefauts", "AutresDefautsComment", "autres"],
];

const toF = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "object") {
    v = v?.$n ?? v?.value ?? v?.S ?? Object.values(v || {})[0];
  }
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const toBool = (v) => {
  if (v === null || v === undefined || v === "") return false;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  return ["true", "vrai", "oui", "yes", "1", "y", "o"].includes(
    String(v).trim().toLowerCase()
  );
};

function normId(raw) {
  if (raw == null) return null;
  const matches = String(raw).match(/\d{5,}/g);
  if (!matches?.length) return null;
  return matches.reduce((a, b) => (a.length >= b.length ? a : b));
}

function parseStackDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;

  // 1) ISO strict (inclut ms + Z) → on laisse Date parser l'UTC
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // 2) Dates avec / ou - : gérer FR (DD/MM/YYYY) et US (MM/DD/YYYY)
  // On essaie de déduire l'ordre quand c'est possible (jour > 12 ou mois > 12).
  const mSlash = s.match(
    /^(\d{2})[\/-](\d{2})[\/-](\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (mSlash) {
    const [, a, b, yyyy, hh = "0", mi = "0", ss = "0"] = mSlash;
    const n1 = Number(a);
    const n2 = Number(b);

    let dd = n1;
    let mm = n2;

    // Déduction : si n1 > 12, c'est forcément le jour (DD/MM)
    if (n1 > 12 && n2 <= 12) {
      dd = n1;
      mm = n2;
    } else if (n2 > 12 && n1 <= 12) {
      // si n2 > 12, c'est forcément le jour (MM/DD)
      dd = n2;
      mm = n1;
    } else {
      // Ambigu (ex: 03/04/2026) : on privilégie FR (DD/MM) car l'app est en FR.
      dd = n1;
      mm = n2;
    }

    const d = new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(mi),
      Number(ss),
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // 3) Fallback : parse natif
  const d = new Date(s.replace("Z", "+00:00"));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateFR(d) {
  if (!d) return "n/c";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

const populationLabel = plisPopulationLabel;
const populationBucket = plisPopulationBucket;

function parseNb(v) {
  if (v == null || v === "") return null;
  if (typeof v === "object") {
    v = v?.$n ?? v?.value ?? v?.N ?? Object.values(v || {})[0];
  }
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function setNbKey(map, key, nb) {
  const k = String(key || "").trim();
  if (!k || !nb) return;
  map[k] = nb;
  const idn = normId(k);
  if (idn) map[idn] = nb;
}

function buildNbpliMap(empList, epaisseurMap) {
  const m = {};

  for (const [stackId, entry] of Object.entries(epaisseurMap || {})) {
    const nb = parseNb(entry?.nbPlis);
    if (nb) setNbKey(m, stackId, nb);
  }

  for (const emp of empList || []) {
    const sid = String(emp.StackId || emp.IDStack || "").trim();
    const nb = parseNb(emp.NbPlis);
    if (nb) setNbKey(m, sid, nb);
  }

  return m;
}

function buildRecord(qsItem, nbpliMap) {
  const idRaw = String(qsItem?.IDStack || qsItem?.StackId || "").trim();
  const idn = normId(idRaw);
  const dt = parseStackDate(qsItem?.Date || qsItem?.Timestamp);

  const nbplis =
    parseNb(qsItem?.NbPlis) ??
    (idn ? nbpliMap[idn] : null) ??
    nbpliMap[String(idRaw).trim()] ??
    null;

  const confRaw = String(qsItem?.Conformite || "").trim().toUpperCase();
  let conf = "?";
  if (confRaw === "CONFORME") conf = "CONFORME";
  else if (confRaw === "NON CONFORME" || confRaw === "NONCONFORME") conf = "NON CONFORME";

  const eps = [1, 2, 3, 4, 5, 6].map((i) => toF(qsItem?.[`Epaisseur${i}`]));
  const defauts = DEFAUTS.map(([champ, champCom, lib]) => {
    const com = qsItem?.[champCom];
    return { lib, present: toBool(qsItem?.[champ]), com: com != null && com !== "" ? String(com) : "" };
  });

  return {
    id: idRaw || idn || "?",
    idNorm: idn,
    date: dt ? formatDateFR(dt) : String(qsItem?.Date || "n/c"),
    ts: dt ? dt.getTime() : null,
    conf,
    eps,
    epMoy: toF(qsItem?.Epaisseur_Moyenne),
    ecartMax: toF(qsItem?.Ecart_Max_Ep),
    defauts,
    nbplis,
    nominal: PLI_TO_NOMINAL[nbplis] ?? null,
    popLabel: populationLabel(nbplis),
    popBucket: populationBucket(nbplis),
    Tcomp: toF(qsItem?.Taux_Compression),
  };
}

const XS = [40, 130, 220];
const TOP = 16;
const BOT = 50;

function scaleFor(rec) {
  const v = rec.eps.filter((x) => x != null);
  if (!v.length) return null;
  let lo = Math.min(...v);
  let hi = Math.max(...v);
  const pad = hi === lo ? 0.2 : Math.max(0.1, (hi - lo) * 0.15);
  return [lo - pad, hi + pad];
}

function mapY(v, smin, smax) {
  if (smax === smin) return (TOP + BOT) / 2;
  return TOP + (1 - (v - smin) / (smax - smin)) * (BOT - TOP);
}

function MiniChart({ vals, color, sc }) {
  if (!sc) {
    return (
      <svg viewBox="0 0 260 70" style={{ width: "100%", height: 60, display: "block" }}>
        <text x="130" y="40" textAnchor="middle" fontSize="11" fill="#64748b">
          pas de mesure
        </text>
      </svg>
    );
  }

  const [smin, smax] = sc;
  const pts = [];
  for (let i = 0; i < 3; i++) {
    if (vals[i] != null) pts.push([XS[i], mapY(vals[i], smin, smax), vals[i]]);
  }

  return (
    <svg viewBox="0 0 260 70" style={{ width: "100%", height: 60, display: "block" }}>
      <line x1="20" y1="58" x2="250" y2="58" stroke="#1e3a50" strokeWidth="1" />
      {pts.length > 1 && (
        <polyline
          points={pts.map((p) => `${p[0]},${p[1].toFixed(1)}`).join(" ")}
          fill="none"
          stroke={color}
          strokeWidth="2"
        />
      )}
      {pts.map(([x, y, v]) => (
        <g key={`${x}-${v}`}>
          <circle cx={x} cy={y} r="3.5" fill={color} />
          <text
            x={x}
            y={Math.max(11, y - 7)}
            textAnchor="middle"
            fontSize="11"
            fill="#94a3b8"
          >
            {v.toFixed(1)}
          </text>
        </g>
      ))}
    </svg>
  );
}

function ConformityBadge({ conf }) {
  if (conf === "CONFORME") {
    return <span style={{ ...LS.badge, ...LS.badgeOk }}>CONFORME</span>;
  }
  if (conf === "NON CONFORME") {
    return <span style={{ ...LS.badge, ...LS.badgeKo }}>NON CONFORME</span>;
  }
  return <span style={{ ...LS.badge, ...LS.badgeUn }}>non renseigné</span>;
}

function StackCard({ rec }) {
  const sc = scaleFor(rec);
  const f2 = (v) => (v == null ? "–" : Number(v).toFixed(2));

  return (
    <div style={LS.card}>
      <div style={LS.cardHd}>
        <span style={LS.cid}>{rec.id}</span>
        <ConformityBadge conf={rec.conf} />
      </div>
      <div style={LS.meta}>
        <span>{rec.date}</span>
        <span>{rec.popLabel}</span>
      </div>
      <div>
        <div style={LS.gbox}>
          <div style={LS.glab}>haut — E1·E3·E5</div>
          <MiniChart vals={[rec.eps[0], rec.eps[2], rec.eps[4]]} color={COL_HAUT} sc={sc} />
        </div>
        <div style={LS.gbox}>
          <div style={LS.glab}>bas — E2·E4·E6</div>
          <MiniChart vals={[rec.eps[1], rec.eps[3], rec.eps[5]]} color={COL_BAS} sc={sc} />
        </div>
        <div style={LS.epline}>
          <span>moy : {f2(rec.epMoy)} mm</span>
          <span>écart max : {f2(rec.ecartMax)} mm</span>
        </div>
      </div>
      <div style={LS.chips}>
        {rec.defauts.map((d) => (
          <span
            key={d.lib}
            style={{ ...LS.chip, ...(d.present ? LS.chipBad : {}) }}
            title={d.com || (d.present ? "défaut signalé" : "RAS")}
          >
            
            {d.lib}
          </span>
        ))}
      </div>
      {rec.Tcomp != null && (
        <div style={LS.meta}>
          <span>Tcomp. {rec.Tcomp}</span>
        </div>
      )}
    </div>
  );
}

function FilterBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ...LS.fbtn, ...(active ? LS.fbtnOn : {}) }}
    >
      {children}
    </button>
  );
}

export default function LastStacksPanel({
  items = [],
  empList = [],
  epaisseurMap = {},
}) {
  const { t, ui } = useI18n();

  const [showCount, setShowCount] = useState(DEFAULT_SHOW);
  const [confFilter, setConfFilter] = useState("all");
  const [popFilter, setPopFilter] = useState("all");

  const records = useMemo(() => {
    const nbpliMap = buildNbpliMap(empList, epaisseurMap);
    const built = (items || []).map((it) => buildRecord(it, nbpliMap));

    const withDate = built.filter((r) => r.ts != null);
    const noDate = built.filter((r) => r.ts == null);
    withDate.sort((a, b) => b.ts - a.ts);
    return [...withDate, ...noDate].slice(0, N_MAX);
  }, [items, empList, epaisseurMap]);

  const visible = useMemo(() => {
    const n =
      showCount === "tous"
        ? records.length
        : Math.min(parseInt(showCount, 10) || DEFAULT_SHOW, records.length);
    return records.slice(0, n).filter((r) => {
      const okConf = confFilter === "all" || r.conf === confFilter;
      const okPop = popFilter === "all" || r.popBucket === popFilter;
      return okConf && okPop;
    });
  }, [records, showCount, confFilter, popFilter]);

  return (
    <div style={LS.wrap}>
      <div style={LS.header}>
        <div>
          <div style={LS.title}>Derniers stacks contrôlés</div>
          <div style={LS.sub}>
            {visible.length} stack(s) affiché(s) — tri par date de contrôle (récent → ancien)
          </div>
        </div>
      </div>

      <div style={LS.bar}>
        <div style={LS.grp}>
          <span style={LS.lbl}>Afficher</span>
          <select
            style={LS.sel}
            value={showCount}
            onChange={(e) => {
              const v = e.target.value;
              setShowCount(v === "tous" ? "tous" : Number(v));
            }}
          >
            {SHOW_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <span style={LS.lbl}>derniers</span>
        </div>

        <div style={LS.grp}>
          <span style={LS.lbl}>Conformité</span>
          <FilterBtn active={confFilter === "all"} onClick={() => setConfFilter("all")}>
            Tous
          </FilterBtn>
          <FilterBtn active={confFilter === "CONFORME"} onClick={() => setConfFilter("CONFORME")}>
            Conforme
          </FilterBtn>
          <FilterBtn
            active={confFilter === "NON CONFORME"}
            onClick={() => setConfFilter("NON CONFORME")}
          >
            Non conforme
          </FilterBtn>
        </div>

        <div style={LS.grp}>
          <span style={LS.lbl}>Épaisseur</span>
          {[
            ["all", "Toutes"],
            ...PLIS_RECONNUS.map((n) => [String(n), `${n} plis`]),
            ["autre", "autre"],
          ].map(([val, label]) => (
            <FilterBtn key={val} active={popFilter === val} onClick={() => setPopFilter(val)}>
              {label}
            </FilterBtn>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <div style={LS.empty}>Aucun stack à afficher.</div>
      ) : (
        <div style={LS.grid}>
          {visible.map((rec) => (
            <StackCard key={`${rec.id}-${rec.date}`} rec={rec} />
          ))}
        </div>
      )}
    </div>
  );
}

const LS = {
  wrap: { display: "flex", flexDirection: "column", gap: 16 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  title: {
    fontSize: 15,
    fontWeight: 800,
    color: "#e2e8f0",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  sub: { fontSize: 12, color: "#64748b", marginTop: 4 },
  bar: {
    display: "flex",
    flexWrap: "wrap",
    gap: "14px 22px",
    alignItems: "center",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 10,
    padding: "12px 16px",
  },
  grp: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  lbl: { fontSize: 12, color: "#64748b", marginRight: 2 },
  fbtn: {
    border: "1px solid #1e3a50",
    background: "rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    fontSize: 12,
    padding: "6px 12px",
    borderRadius: 7,
    cursor: "pointer",
    fontWeight: 600,
  },
  fbtnOn: {
    background: "#E6FF3E",
    color: "#041e2e",
    borderColor: "#E6FF3E",
  },
  sel: {
    height: 34,
    border: "1px solid #1e3a50",
    borderRadius: 7,
    background: "#082131",
    fontSize: 13,
    padding: "0 10px",
    color: "#e2e8f0",
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))",
    gap: 18,
  },
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 12,
    padding: "15px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 11,
  },
  cardHd: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  cid: {
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
    color: "#E6FF3E",
  },
  badge: {
    fontSize: 10,
    fontWeight: 700,
    padding: "3px 10px",
    borderRadius: 6,
    whiteSpace: "nowrap",
  },
  badgeOk: { background: "rgba(34,197,94,0.15)", color: "#4ade80" },
  badgeKo: { background: "rgba(248,113,113,0.15)", color: "#f87171" },
  badgeUn: { background: "rgba(255,255,255,0.06)", color: "#94a3b8" },
  meta: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    color: "#64748b",
  },
  gbox: {
    border: "1px solid #1e3a50",
    borderRadius: 8,
    padding: "9px 9px 3px",
    marginTop: 7,
  },
  glab: { fontSize: 11, color: "#64748b", marginBottom: 1 },
  epline: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    color: "#94a3b8",
    marginTop: 4,
    paddingTop: 8,
    borderTop: "1px solid #1e3a50",
  },
  chips: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: {
    fontSize: 11,
    padding: "3px 9px",
    borderRadius: 6,
    background: "rgba(255,255,255,0.05)",
    color: "#94a3b8",
  },
  chipBad: {
    background: "rgba(248,113,113,0.12)",
    color: "#f87171",
    fontWeight: 600,
  },
  empty: {
    color: "#64748b",
    fontSize: 14,
    padding: 48,
    textAlign: "center",
    border: "1px dashed #1e3a50",
    borderRadius: 12,
  },
};
